import React from 'react';
import {Detail, FlexRow, Icon, Label, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {DELETED_USER, describeActivity} from './quoteActivityCopy';
import {RoundAvatar, Timeline, TimelineBody, TimelineEntry, TimelineIcon} from './QuoteActivityList.styled';

export type QuoteActivityItem = {
	id: string;
	eventType: string | null;
	/** The quote revision this event belongs to; separates a first publish from a republish. */
	revisionNumber: number | null;
	createdAt: string | null;
	clientContactName: string | null;
	reason: string | null;
	person: {id: string; fullName: string | null} | null;
};

type Props = {activities: QuoteActivityItem[]};

/**
 * The rail icon per event, tinted to the outcome: approved reads success, declined error. A
 * revision is neither — it reopens the quote rather than resolving it — so it takes the neutral
 * tint and the pencil that names the editing it hands back to the user.
 *
 * Every event type the log can render needs an entry here. The fallback below keeps an unknown
 * type from crashing, but it silently borrows PUBLISHED's icon, so a missing entry shows the wrong
 * glyph rather than an obvious blank.
 */
const EVENT_ICON: {[eventType: string]: {icon: string; color: string}} = {
	PUBLISHED: {icon: 'note', color: 'primary'},
	APPROVED: {icon: 'checkCircle', color: 'success'},
	DECLINED: {icon: 'closeCircle', color: 'error'},
	REVISING: {icon: 'edit', color: 'default'},
};

/** Enough to place the event without competing with the entry title. */
const TIMESTAMP_FORMAT: Intl.DateTimeFormatOptions = {
	year: 'numeric',
	month: 'short',
	day: 'numeric',
	hour: '2-digit',
	minute: '2-digit',
};

/**
 * The audit log, rendered in the order given — the company service already sorted it newest first
 * (with an id tiebreak, because publish-then-decide can land inside the same second), so this does
 * no sorting of its own and cannot disagree with the server about the order.
 *
 * Presentational: it takes a plain array, not a fragment reference. The Relay boundary is one level
 * up in QuoteActivitySection.
 *
 * The avatar deliberately shows initials rather than the person's picture. Avatar's props are
 * name/src/size only, so a picture URL could not also carry the `data-private` attribute that keeps
 * an authenticated asset URL out of session recordings.
 */
export const QuoteActivityList = ({activities}: Props): JSX.Element => {
	const {formatMessage, formatDate} = useIntl();

	const rendered = activities
		.map(activity => ({
			activity,
			copy: describeActivity({
				eventType: activity.eventType,
				revisionNumber: activity.revisionNumber,
				personName: activity.person ? activity.person.fullName : null,
				clientContactName: activity.clientContactName,
				reason: activity.reason,
			}),
		}))
		// An unknown event type yields null copy and is skipped, so an older client tolerates an
		// event type a newer backend writes.
		.filter((entry): entry is {activity: QuoteActivityItem; copy: NonNullable<typeof entry.copy>} => entry.copy !== null);

	if (rendered.length === 0) {
		return <Text>{formatMessage({id: 'quote_activity.empty'})}</Text>;
	}

	// The log reads as a chronology: created at the top, the outcome at the bottom. The server
	// sorts newest-first and that stays, because a paginated `first: N` must return the most recent
	// N rather than the oldest — so the reversal belongs here, at the point of display.
	const oldestFirst = rendered.slice().reverse();

	return (
		<Timeline>
			{oldestFirst.map(({activity, copy}) => {
				const person =
					copy.person === DELETED_USER ? formatMessage({id: 'quote_activity.unknown_person'}) : copy.person;
				const rail = EVENT_ICON[activity.eventType as string] || EVENT_ICON.PUBLISHED;
				return (
					<TimelineEntry key={activity.id} data-cy="quote-activity-entry">
						<TimelineIcon>
							<Icon icon={rail.icon} size="m" color={rail.color} />
						</TimelineIcon>
						<TimelineBody>
							<Label>{formatMessage({id: copy.titleMessageId})}</Label>
							<FlexRow gap="s" alignItems="center">
								<RoundAvatar name={person} size="s" />
								<Text>{person}</Text>
								{copy.contact && (
									<>
										<Text size="1" color="auxiliary">
											&middot;
										</Text>
										<Text size="1" color="auxiliary">
											{formatMessage({id: 'quote_activity.contact_prefix'})}
										</Text>
										<Label>{copy.contact}</Label>
									</>
								)}
							</FlexRow>
							{activity.reason && (
								<Text size="1" color="auxiliary">
									{formatMessage({id: 'quote_activity.reason_prefix'}, {reason: activity.reason})}
								</Text>
							)}
						</TimelineBody>
						{activity.createdAt && <Detail noWrap>{formatDate(activity.createdAt, TIMESTAMP_FORMAT)}</Detail>}
					</TimelineEntry>
				);
			})}
		</Timeline>
	);
};
